/**
 * Receipt and document printing.
 *
 * Three routes, picked in order of what the device actually offers:
 *
 *   1. Android (Capacitor) with a Bluetooth thermal printer paired — the ESC/POS
 *      bytes the server produced go straight to the device.
 *   2. A browser with WebUSB and a USB thermal printer — same bytes, over USB.
 *   3. Anything else — a print window laid out to the roll width, which also
 *      covers A4 for an office printer and "save as PDF" for sending a copy.
 *
 * The bytes are produced once, on the server, so a receipt reprinted from a
 * phone is byte-identical to the one the counter printed.
 */

import type { ReceiptPayload } from './api.js';

/** True when running inside the Capacitor Android shell rather than a browser tab. */
export function isNativeAndroid(): boolean {
  return typeof window !== 'undefined' && Boolean((window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.());
}

interface BluetoothPrinterPlugin {
  list(): Promise<{ devices: { name: string; address: string }[] }>;
  connect(options: { address: string }): Promise<void>;
  write(options: { data: string }): Promise<void>;
}

function bluetoothPlugin(): BluetoothPrinterPlugin | null {
  const capacitor = (window as { Capacitor?: { Plugins?: Record<string, unknown> } }).Capacitor;
  return (capacitor?.Plugins?.ThermalPrinter as BluetoothPrinterPlugin | undefined) ?? null;
}

export interface PrintResult {
  ok: boolean;
  route: 'bluetooth' | 'usb' | 'window';
  error?: string;
}

export async function printReceipt(receipt: ReceiptPayload): Promise<PrintResult> {
  if (isNativeAndroid()) {
    const plugin = bluetoothPlugin();
    const address = localStorage.getItem('bakery.printer.address');
    if (plugin && address) {
      try {
        await plugin.connect({ address });
        await plugin.write({ data: receipt.escpos });
        return { ok: true, route: 'bluetooth' };
      } catch (error) {
        // Fall through to the print window rather than losing the sale's receipt.
        console.warn('Bluetooth print failed, falling back to a print window', error);
      }
    }
  }

  const usb = await tryWebUsb(receipt);
  if (usb) return usb;

  openPrintWindow(receiptHtml(receipt), `Receipt ${receipt.receiptNo}`);
  return { ok: true, route: 'window' };
}

/** WebUSB is Chromium-only and needs a user gesture; failure here is expected. */
async function tryWebUsb(receipt: ReceiptPayload): Promise<PrintResult | null> {
  const usb = (navigator as Navigator & { usb?: { getDevices(): Promise<USBDeviceLike[]> } }).usb;
  if (!usb) return null;

  try {
    const devices = await usb.getDevices();
    const printer = devices[0];
    if (!printer) return null;

    await printer.open();
    if (printer.configuration === null) await printer.selectConfiguration(1);
    await printer.claimInterface(0);

    const bytes = Uint8Array.from(atob(receipt.escpos), (character) => character.charCodeAt(0));
    await printer.transferOut(1, bytes);
    return { ok: true, route: 'usb' };
  } catch (error) {
    console.warn('USB print unavailable', error);
    return null;
  }
}

interface USBDeviceLike {
  open(): Promise<void>;
  configuration: unknown;
  selectConfiguration(value: number): Promise<void>;
  claimInterface(value: number): Promise<void>;
  transferOut(endpoint: number, data: Uint8Array): Promise<unknown>;
}

/**
 * Render the monospace receipt at the roll's real width so what prints matches
 * what the thermal printer would produce.
 */
function receiptHtml(receipt: ReceiptPayload): string {
  const widthMm = receipt.paperWidthMm;
  return `
    <style>
      @page { size: ${widthMm}mm auto; margin: 3mm; }
      body {
        font-family: 'JetBrains Mono', ui-monospace, 'Courier New', monospace;
        font-size: ${widthMm === 58 ? '10.5px' : '11.5px'};
        line-height: 1.35;
        white-space: pre;
        margin: 0;
        color: #000;
      }
      @media screen {
        body {
          width: ${widthMm}mm;
          margin: 24px auto;
          padding: 12px;
          background: #fff;
          box-shadow: 0 2px 12px rgba(0,0,0,.15);
        }
      }
    </style>
    <body>${escapeHtml(receipt.text)}</body>
  `;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Open a hidden iframe, print it, and clean up.
 * An iframe rather than window.open, because pop-up blockers eat the latter and
 * a blocked receipt is a lost receipt.
 */
export function openPrintWindow(html: string, title: string): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(frame);

  const doc = frame.contentDocument;
  if (!doc) {
    document.body.removeChild(frame);
    return;
  }

  doc.open();
  doc.write(`<!doctype html><html><head><title>${escapeHtml(title)}</title></head>${html}</html>`);
  doc.close();

  const run = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    // Give the print dialog time to take its snapshot before the frame goes.
    setTimeout(() => frame.parentNode && document.body.removeChild(frame), 1000);
  };

  if (frame.contentWindow?.document.readyState === 'complete') run();
  else frame.onload = run;
}

// --- Payslips --------------------------------------------------------------

export interface PayslipPrintData {
  payslipNo?: string;
  business: { tradingName: string; legalName: string; tin: string; addressLine1: string; addressLine2: string | null; city: string; province: string; postalCode: string; vatRegistered: boolean };
  employeeName: string;
  /** Their job title, printed under the name. */
  position?: string | null;
  issuedOn: string;
  periodStart: string;
  periodEnd: string;
  lines: {
    label: string;
    unitLabel: string;
    qty: number;
    rate: number;
    amount: number;
    breakdown: { date: string; qty: number }[];
    kind?: 'earning' | 'holiday' | 'absence';
    note?: string | null;
  }[];
  adjustments: { label: string; amount: number; kind: 'addition' | 'deduction' }[];
  subtotal: number;
  grandTotal: number;
}

const money = (centavos: number) => (centavos / 100).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-PH', { day: 'numeric', month: 'short', timeZone: 'UTC' });

const longDate = (iso: string) =>
  new Date(iso.length > 10 ? iso : `${iso}T00:00:00Z`).toLocaleDateString('en-PH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: iso.length > 10 ? undefined : 'UTC',
  });

/**
 * The shop's wage payslip, for anyone paid by the day, the shift or the month.
 *
 * Set out in the blocks the shop's own slip uses — earnings, absences, holiday
 * pay, then net pay and an acknowledgement — rather than the piece-rate grid,
 * which has nothing to put in its columns when the answer is "eleven days".
 *
 * Returns the HTML rather than printing it, so the layout can be checked
 * without a browser. What goes on a payslip is not something to verify by eye
 * once and hope.
 */
export function buildWageSlipHtml(data: PayslipPrintData): string {
  const { business } = data;

  const earnings = data.lines.filter((line) => line.kind !== 'absence' && line.kind !== 'holiday');
  const holiday = data.lines.filter((line) => line.kind === 'holiday');
  const absences = data.lines.filter((line) => line.kind === 'absence');

  const grossPay = sumOf(earnings.map((line) => line.amount));
  const additions = data.adjustments.filter((a) => a.kind === 'addition');
  const deductions = data.adjustments.filter((a) => a.kind === 'deduction');

  /** A block with its own shaded heading, as on the paper slip. */
  const section = (title: string, rows: string, total?: number) => `
    <tr class="band"><th colspan="3">${escapeHtml(title)}</th></tr>
    ${rows}
    ${total === undefined ? '' : `<tr><td class="strong" colspan="2"></td><td class="num strong">₱${money(total)}</td></tr>`}
  `;

  const rateRow = (label: string, qty: string, amount: string, strong = false) => `
    <tr>
      <th class="row-label${strong ? ' strong' : ''}">${escapeHtml(label)}</th>
      <td class="num${strong ? ' strong' : ''}">${qty}</td>
      <td class="num${strong ? ' strong' : ''}">${amount}</td>
    </tr>
  `;

  const earningRows = earnings
    .map((line) =>
      rateRow(line.label, `${line.qty} ${escapeHtml(line.unitLabel)}`, `₱${money(line.rate)}`),
    )
    .join('');

  const holidayRows =
    holiday.length > 0
      ? holiday
          .map((line) => rateRow(line.label, `${line.qty} ${escapeHtml(line.unitLabel)}`, `₱${money(line.amount)}`))
          .join('')
      : rateRow('Regular Holidays', '0 days', '₱0.00') + rateRow('Special Non-Wk Days', '0 days', '₱0.00');

  const absenceRows =
    absences.length > 0
      ? absences
          .map(
            (line) =>
              rateRow('Shifts Absent', `${line.qty} ${escapeHtml(line.unitLabel)}`, '₱0.00') +
              `<tr><th class="row-label">Reason</th><td class="num" colspan="2">${escapeHtml(line.note ?? 'n/a')}</td></tr>`,
          )
          .join('')
      : rateRow('Shifts Absent', '0 days', '₱0.00') +
        '<tr><th class="row-label">Reason</th><td class="num" colspan="2">n/a</td></tr>';

  const adjustmentRows = (rows: typeof data.adjustments) =>
    rows.map((a) => rateRow(a.label, '', `₱${money(a.amount)}`)).join('');

  const html = `
    <style>
      @page { size: A4; margin: 16mm; }
      body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; margin: 0; }
      h1 { font-size: 12pt; margin: 0; text-align: center; }
      .sub { font-size: 10pt; margin: 2px 0 0; text-align: center; }
      .meta { font-size: 9.5pt; margin: 2px 0 0; text-align: center; line-height: 1.5; }
      .title { text-align: center; font-weight: bold; letter-spacing: .16em; margin: 20px 0 16px; }
      .who { font-size: 10pt; margin: 0 0 14px; }
      .who td { padding: 2px 0; border: none; }
      .who .k { font-weight: bold; width: 150px; }
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #999; padding: 4px 8px; font-size: 10pt; }
      .band th { background: #c9c9c9; text-align: center; letter-spacing: .08em; font-size: 9.5pt; }
      .row-label { text-align: left; font-weight: normal; }
      .num { text-align: center; font-variant-numeric: tabular-nums; }
      .strong { font-weight: bold; background: #f2f2f2; }
      .net th, .net td { background: #c9c9c9; font-weight: bold; font-size: 11pt; }
      .ack { margin-top: 26px; text-align: center; }
      .ack .h { font-weight: bold; letter-spacing: .05em; font-size: 10pt; }
      .ack .i { font-style: italic; font-size: 9.5pt; margin-top: 2px; }
      .sign { margin-top: 22px; font-size: 10pt; }
      .sign .line { margin-top: 30px; border-top: 1px solid #000; width: 250px; }
      .sign .f { margin-top: 18px; font-weight: bold; }
      .sign .v { color: #555; }
      .foot { margin-top: 30px; text-align: center; font-weight: bold; font-style: italic; font-size: 10pt; }
    </style>
    <body>
      <h1>${escapeHtml(business.tradingName.toUpperCase())}</h1>
      <p class="sub">Owned and Operated by ${escapeHtml(business.legalName)}</p>
      <p class="meta">
        ${business.vatRegistered ? 'VAT' : 'Non-VAT'} Reg. TIN: ${escapeHtml(business.tin)}<br />
        ${escapeHtml(business.addressLine1)}${business.addressLine2 ? `, ${escapeHtml(business.addressLine2)}` : ''},
        ${escapeHtml(business.city)}, ${escapeHtml(business.province)} ${escapeHtml(business.postalCode)}
      </p>

      <p class="title">PAYSLIP</p>

      <table class="who">
        <tr><td class="k">Date:</td><td>${escapeHtml(longDate(data.issuedOn))}</td></tr>
        <tr><td class="k">Employee Name:</td><td><strong>${escapeHtml(data.employeeName)}</strong></td></tr>
        ${data.position ? `<tr><td class="k">Position:</td><td>${escapeHtml(data.position)}</td></tr>` : ''}
        <tr><td class="k">Pay Period:</td><td>${escapeHtml(longDate(data.periodStart))} — ${escapeHtml(longDate(data.periodEnd))}</td></tr>
        ${data.payslipNo ? `<tr><td class="k">Payslip No:</td><td>${escapeHtml(data.payslipNo)}</td></tr>` : ''}
      </table>

      <table>
        ${section('EARNINGS', earningRows + rateRow('Gross Pay', '', `₱${money(grossPay)}`, true))}
        ${section('ABSENCES', absenceRows, 0)}
        ${section('HOLIDAY PAY', holidayRows, sumOf(holiday.map((line) => line.amount)))}
        ${additions.length > 0 ? section('ADDITIONS', adjustmentRows(additions), sumOf(additions.map((a) => a.amount))) : ''}
        ${deductions.length > 0 ? section('DEDUCTIONS', adjustmentRows(deductions), sumOf(deductions.map((a) => a.amount))) : ''}
        <tr class="net"><th class="row-label">NET PAY</th><td class="num" colspan="2">₱${money(data.grandTotal)}</td></tr>
      </table>

      <div class="ack">
        <div class="h">ACKNOWLEDGEMENT &amp; CERTIFICATION</div>
        <div class="i">I acknowledge receipt of the above-stated net pay amount.</div>
      </div>

      <div class="sign">
        <div class="f">Employee Signature:</div>
        <div class="line"></div>
        <div class="f">Print Name:</div>
        <div class="v">${escapeHtml(data.employeeName)}</div>
        <div class="f">Date:</div>
        <div class="v">${escapeHtml(longDate(data.issuedOn))}</div>
      </div>

      <p class="foot">FOR ACCOUNTING &amp; BIR RECORDS</p>
    </body>
  `;

  return html;
}

function printWageSlip(data: PayslipPrintData): void {
  openPrintWindow(buildWageSlipHtml(data), `Payslip — ${data.employeeName}`);
}

const sumOf = (values: number[]) => values.reduce((total, value) => total + value, 0);

/**
 * Reproduces the bakery's existing paper payslip: the header block, a column per
 * pay item with the daily counts down the side, the fee totals, then any
 * additions, the grand total and a signature line.
 */
export function printPayslip(data: PayslipPrintData): void {
  /*
   * Two slips, one function.
   *
   * The piece-rate slip is a grid: a column per rate with the day's counts
   * down the side, which is how the bakery has always paid its bakers. A
   * wage slip has nothing to put in that grid — one line reading "11 days" —
   * so it is set out the way the shop's own wage payslip is, in blocks:
   * earnings, absences, holiday pay, then net pay and a signature.
   */
  if (data.lines.some((line) => line.kind)) return printWageSlip(data);

  const { business } = data;

  // Union of every date across all pay items, so the grid has one row per day.
  const dates = [...new Set(data.lines.flatMap((line) => line.breakdown.map((entry) => entry.date)))].sort();

  const gridHeader = data.lines
    .map((line) => `<th class="num">${escapeHtml(line.label)}<span class="rate">₱${money(line.rate)} / ${escapeHtml(line.unitLabel.replace(/s$/, ''))}</span></th>`)
    .join('');

  const gridRows = dates
    .map((date) => {
      const cells = data.lines
        .map((line) => {
          const entry = line.breakdown.find((item) => item.date === date);
          return `<td class="num">${entry ? entry.qty : ''}</td>`;
        })
        .join('');
      return `<tr><th class="day">${shortDate(date)}</th>${cells}</tr>`;
    })
    .join('');

  const totalsRow = data.lines.map((line) => `<td class="num strong">${line.qty}</td>`).join('');
  const feesRow = data.lines.map((line) => `<td class="num strong">${money(line.amount)}</td>`).join('');

  const adjustmentRows = data.adjustments
    .map(
      (adjustment) =>
        `<tr><th class="day">${escapeHtml(adjustment.label)}:</th><td class="num strong" colspan="${Math.max(data.lines.length, 1)}">${
          adjustment.kind === 'deduction' ? '-' : ''
        }${money(adjustment.amount)}</td></tr>`,
    )
    .join('');

  const html = `
    <style>
      @page { size: A4; margin: 16mm; }
      body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; margin: 0; }
      h1 { font-size: 13pt; margin: 0; letter-spacing: .01em; }
      .sub { font-size: 10pt; font-weight: bold; margin: 2px 0 0; }
      .meta { font-size: 9.5pt; margin: 2px 0 0; line-height: 1.4; }
      .title { text-align: center; font-weight: bold; letter-spacing: .18em; margin: 26px 0 18px; }
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #000; padding: 4px 7px; font-size: 10pt; }
      thead th { background: #f0ece4; text-align: center; }
      .num { text-align: center; font-variant-numeric: tabular-nums; }
      .day { text-align: right; width: 128px; font-weight: normal; }
      .strong { font-weight: bold; background: #f7f4ee; }
      .rate { display: block; font-weight: normal; font-size: 8.5pt; color: #444; }
      .grand td, .grand th { background: #ece5d8; font-weight: bold; font-size: 11pt; }
      .head-row th { text-align: left; }
      .sign { margin-top: 54px; font-size: 10pt; }
      .sign .line { margin-top: 34px; border-top: 1px solid #000; width: 260px; padding-top: 4px; }
      .no-border td, .no-border th { border: none; }
    </style>
    <body>
      <h1>${escapeHtml(business.tradingName.toUpperCase())}</h1>
      <p class="sub">Owned and Operated by ${escapeHtml(business.legalName)}</p>
      <p class="meta">
        ${business.vatRegistered ? 'VAT' : 'Non-VAT'} Reg. TIN: ${escapeHtml(business.tin)}<br />
        ${escapeHtml(business.addressLine1)}${business.addressLine2 ? `, ${escapeHtml(business.addressLine2)}` : ''},
        ${escapeHtml(business.city)}, ${escapeHtml(business.province)} ${escapeHtml(business.postalCode)}
      </p>

      <p class="title">PAYSLIP</p>

      <table>
        <tr class="head-row">
          <th>Date: ${escapeHtml(new Date(data.issuedOn).toLocaleDateString('en-PH'))}</th>
          <th colspan="${Math.max(data.lines.length, 1)}">Name: ${escapeHtml(data.employeeName)}</th>
        </tr>
        <thead>
          <tr><th class="day">Period ${shortDate(data.periodStart)} – ${shortDate(data.periodEnd)}</th>${gridHeader}</tr>
        </thead>
        <tbody>
          ${gridRows}
          <tr><th class="day strong">Total goods:</th>${totalsRow}</tr>
          <tr><th class="day strong">Fee total:</th>${feesRow}</tr>
          ${adjustmentRows}
          <tr class="grand"><th class="day">Grand total:</th><td class="num" colspan="${Math.max(data.lines.length, 1)}">${money(data.grandTotal)}</td></tr>
        </tbody>
      </table>

      <div class="sign">
        Received by:
        <div class="line">${escapeHtml(data.employeeName)}</div>
      </div>
    </body>
  `;

  openPrintWindow(html, `Payslip — ${data.employeeName}`);
}
